import {
  displayPersonName,
  FAMILY_TREE_GROUPS,
  type FamilyLink,
  type FamilyPerson,
  type FamilyTreeData,
} from './family-tree-types';

export interface DescendantBranch {
  link: FamilyLink;
  descendants: DescendantBranch[];
}

export interface ParentBranch {
  parent: FamilyLink;
  grandparents: FamilyLink[];
  uncles: Array<{
    link: FamilyLink;
    cousins: DescendantBranch[];
  }>;
}

export interface SiblingBranch {
  sibling: FamilyLink;
  descendants: DescendantBranch[];
}

export interface FamilyTreeLayout {
  centerPerson: FamilyPerson;
  parentBranches: ParentBranch[];
  siblingBranches: SiblingBranch[];
  spouses: FamilyLink[];
  centerDescendants: DescendantBranch[];
  unplaced: FamilyLink[];
  peopleById: Map<string, FamilyPerson>;
}

const allLinks = (tree: FamilyTreeData): FamilyLink[] =>
  FAMILY_TREE_GROUPS.flatMap((group) => tree[group.key]);

const buildDescendantBranches = (
  links: FamilyLink[],
  byAnchor: Map<string, FamilyLink[]>,
): DescendantBranch[] =>
  links.map((link) => ({
    link,
    descendants: buildDescendantBranches(byAnchor.get(link.related.id) ?? [], byAnchor),
  }));

export const buildFamilyTreeLayout = (
  centerPerson: FamilyPerson,
  tree: FamilyTreeData,
): FamilyTreeLayout => {
  const links = allLinks(tree);
  const peopleById = new Map<string, FamilyPerson>([[centerPerson.id, centerPerson]]);
  for (const link of links) {
    peopleById.set(link.related.id, link.related);
  }

  const byAnchor = new Map<string, FamilyLink[]>();

  for (const link of links) {
    const anchorId = link.attachedToMemberId;
    if (!anchorId) continue;
    const bucket = byAnchor.get(anchorId) ?? [];
    bucket.push(link);
    byAnchor.set(anchorId, bucket);
  }

  const parentBranches: ParentBranch[] = tree.parents.map((parent) => ({
    parent,
    grandparents: (byAnchor.get(parent.related.id) ?? []).filter(
      (link) => link.relationship === 'grandparent',
    ),
    uncles: tree.unclesAunts
      .filter((link) => link.attachedToMemberId === parent.related.id)
      .map((uncle) => ({
        link: uncle,
        cousins: buildDescendantBranches(
          (byAnchor.get(uncle.related.id) ?? []).filter((link) => link.relationship === 'cousin'),
          byAnchor,
        ),
      })),
  }));

  const siblingBranches: SiblingBranch[] = tree.siblings.map((sibling) => ({
    sibling,
    descendants: buildDescendantBranches(
      (byAnchor.get(sibling.related.id) ?? []).filter(
        (link) => link.relationship === 'niece_nephew' || link.relationship === 'child',
      ),
      byAnchor,
    ),
  }));

  const centerDescendants = buildDescendantBranches(
    (byAnchor.get(centerPerson.id) ?? []).filter((link) => link.relationship === 'child'),
    byAnchor,
  );

  const placedIds = new Set<string>();
  const markPlaced = (link: FamilyLink) => placedIds.add(link.related.id);
  for (const branch of parentBranches) {
    markPlaced(branch.parent);
    branch.grandparents.forEach(markPlaced);
    branch.uncles.forEach((uncle) => {
      markPlaced(uncle.link);
      const walk = (nodes: DescendantBranch[]) => {
        for (const node of nodes) {
          markPlaced(node.link);
          walk(node.descendants);
        }
      };
      walk(uncle.cousins);
    });
  }
  siblingBranches.forEach((branch) => {
    markPlaced(branch.sibling);
    const walk = (nodes: DescendantBranch[]) => {
      for (const node of nodes) {
        markPlaced(node.link);
        walk(node.descendants);
      }
    };
    walk(branch.descendants);
  });
  tree.spouses.forEach(markPlaced);
  const walkCenter = (nodes: DescendantBranch[]) => {
    for (const node of nodes) {
      markPlaced(node.link);
      walkCenter(node.descendants);
    }
  };
  walkCenter(centerDescendants);

  const remaining = links.filter((link) => !placedIds.has(link.related.id));

  return {
    centerPerson,
    parentBranches,
    siblingBranches,
    spouses: tree.spouses,
    centerDescendants,
    unplaced: remaining,
    peopleById,
  };
};

export const anchorPersonName = (
  layout: FamilyTreeLayout,
  memberId: string | null | undefined,
): string | null => {
  if (!memberId) return null;
  const person = layout.peopleById.get(memberId);
  return person ? displayPersonName(person) : null;
};

export interface HierarchicalListRow {
  link: FamilyLink;
  depth: number;
  anchorName: string | null;
  isCenter?: boolean;
}

export const buildHierarchicalListRows = (
  centerPerson: FamilyPerson,
  tree: FamilyTreeData,
): HierarchicalListRow[] => {
  const layout = buildFamilyTreeLayout(centerPerson, tree);
  const rows: HierarchicalListRow[] = [];

  const pushBranch = (nodes: DescendantBranch[], depth: number, anchorName: string | null) => {
    for (const node of nodes) {
      rows.push({ link: node.link, depth, anchorName });
      pushBranch(
        node.descendants,
        depth + 1,
        displayPersonName(node.link.related),
      );
    }
  };

  for (const branch of layout.parentBranches) {
    for (const grandparent of branch.grandparents) {
      rows.push({
        link: grandparent,
        depth: 0,
        anchorName: displayPersonName(branch.parent.related),
      });
    }
    rows.push({ link: branch.parent, depth: 0, anchorName: null });
    for (const uncle of branch.uncles) {
      rows.push({
        link: uncle.link,
        depth: 0,
        anchorName: displayPersonName(branch.parent.related),
      });
      pushBranch(uncle.cousins, 1, displayPersonName(uncle.link.related));
    }
  }

  for (const branch of layout.siblingBranches) {
    rows.push({ link: branch.sibling, depth: 0, anchorName: null });
    pushBranch(branch.descendants, 1, displayPersonName(branch.sibling.related));
  }

  rows.push({
    link: {
      id: `center:${centerPerson.id}`,
      relationship: 'other',
      notes: null,
      related: centerPerson,
    },
    depth: 0,
    anchorName: null,
    isCenter: true,
  });

  for (const spouse of layout.spouses) {
    rows.push({ link: spouse, depth: 0, anchorName: null });
  }

  pushBranch(layout.centerDescendants, 1, displayPersonName(centerPerson));

  for (const link of layout.unplaced) {
    rows.push({
      link,
      depth: 0,
      anchorName: anchorPersonName(layout, link.attachedToMemberId),
    });
  }

  return rows;
};
